import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 55 };
const RSI_HEIGHT = 180;
const MACD_HEIGHT = 180;
const GAP = 30;
const TOTAL_HEIGHT = RSI_HEIGHT + GAP + MACD_HEIGHT;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function PriceMomentumChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const safeData = useMemo(() => {
    if (!data || !data.dates || data.dates.length === 0) return null;
    if (!data.rsi || !data.macd) return null;
    return data;
  }, [data]);

  useEffect(() => {
    if (!safeData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const rsiInnerH = RSI_HEIGHT - MARGIN.top - MARGIN.bottom;
    const macdInnerH = MACD_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('width', width)
      .attr('height', TOTAL_HEIGHT)
      .attr('viewBox', `0 0 ${width} ${TOTAL_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const overbought = safeData.overbought != null ? safeData.overbought : 70;
    const oversold = safeData.oversold != null ? safeData.oversold : 30;

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    // ──────────────────────────────────────────
    // Shared X scale
    // ──────────────────────────────────────────
    const x = d3.scalePoint()
      .domain(safeData.dates)
      .range([0, innerW])
      .padding(0.5);

    // ──────────────────────────────────────────
    // TOP PANEL — RSI
    // ──────────────────────────────────────────
    const rsiG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // RSI panel label
    rsiG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('RSI');

    const yRsi = d3.scaleLinear()
      .domain([0, 100])
      .range([rsiInnerH, 0]);

    // Horizontal grid lines for RSI
    const rsiTicks = [0, 20, 30, 50, 70, 80, 100];
    rsiG.selectAll('.rsi-grid')
      .data(rsiTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yRsi(d))
      .attr('y2', (d) => yRsi(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Overbought shaded zone (above overbought level)
    rsiG.append('rect')
      .attr('x', 0)
      .attr('y', yRsi(100))
      .attr('width', innerW)
      .attr('height', yRsi(overbought) - yRsi(100))
      .attr('fill', '#fecaca')
      .attr('fill-opacity', 0.25);

    // Oversold shaded zone (below oversold level)
    rsiG.append('rect')
      .attr('x', 0)
      .attr('y', yRsi(oversold))
      .attr('width', innerW)
      .attr('height', yRsi(0) - yRsi(oversold))
      .attr('fill', '#bbf7d0')
      .attr('fill-opacity', 0.25);

    // Overbought reference line
    rsiG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yRsi(overbought))
      .attr('y2', yRsi(overbought))
      .attr('stroke', '#dc2626')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Oversold reference line
    rsiG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yRsi(oversold))
      .attr('y2', yRsi(oversold))
      .attr('stroke', '#16a34a')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Overbought / oversold labels
    rsiG.append('text')
      .attr('x', innerW + 4)
      .attr('y', yRsi(overbought) + 4)
      .attr('font-size', '9px')
      .attr('fill', '#dc2626')
      .text(overbought);

    rsiG.append('text')
      .attr('x', innerW + 4)
      .attr('y', yRsi(oversold) + 4)
      .attr('font-size', '9px')
      .attr('fill', '#16a34a')
      .text(oversold);

    // RSI Y axis
    rsiG.append('g')
      .call(d3.axisLeft(yRsi).tickValues([0, 30, 50, 70, 100]).tickSize(0))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    rsiG.selectAll('.domain').attr('stroke', '#e5e7eb');
    rsiG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // RSI line
    const rsiPoints = safeData.dates
      .map((date, i) => ({
        date,
        value: safeData.rsi[i],
      }))
      .filter((d) => d.value !== null && d.value !== undefined);

    if (rsiPoints.length > 1) {
      const rsiLine = d3.line()
        .x((d) => x(d.date))
        .y((d) => yRsi(d.value))
        .curve(d3.curveMonotoneX);

      rsiG.append('path')
        .datum(rsiPoints)
        .attr('d', rsiLine)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2);
    }

    // ──────────────────────────────────────────
    // BOTTOM PANEL — MACD
    // ──────────────────────────────────────────
    const macdTopY = RSI_HEIGHT + GAP;
    const macdG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${macdTopY})`);

    // MACD panel label
    macdG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('MACD');

    // Determine MACD Y domain from all MACD values
    const allMacdValues = [];
    if (safeData.macd.line) safeData.macd.line.forEach((v) => { if (v != null) allMacdValues.push(v); });
    if (safeData.macd.signal) safeData.macd.signal.forEach((v) => { if (v != null) allMacdValues.push(v); });
    if (safeData.macd.histogram) safeData.macd.histogram.forEach((v) => { if (v != null) allMacdValues.push(v); });

    const macdExtent = d3.extent(allMacdValues);
    const macdPadding = ((macdExtent[1] || 0) - (macdExtent[0] || 0)) * 0.15 || 1;

    const yMacd = d3.scaleLinear()
      .domain([(macdExtent[0] || 0) - macdPadding, (macdExtent[1] || 0) + macdPadding])
      .nice()
      .range([macdInnerH, 0]);

    // MACD grid lines
    const macdTicks = yMacd.ticks(5);
    macdG.selectAll('.macd-grid')
      .data(macdTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yMacd(d))
      .attr('y2', (d) => yMacd(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // MACD zero line
    macdG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yMacd(0))
      .attr('y2', yMacd(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1);

    // MACD X axis (shared date labels at the bottom)
    macdG.append('g')
      .attr('transform', `translate(0, ${macdInnerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDate))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    macdG.select('.domain').attr('stroke', '#e5e7eb');

    // MACD Y axis
    macdG.append('g')
      .call(d3.axisLeft(yMacd).ticks(5).tickSize(0))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    macdG.selectAll('.domain').attr('stroke', '#e5e7eb');
    macdG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // MACD histogram bars
    if (safeData.macd.histogram) {
      const barSpacing = innerW / safeData.dates.length;
      const barWidth = Math.max(2, barSpacing * 0.5);

      safeData.dates.forEach((date, i) => {
        const val = safeData.macd.histogram[i];
        if (val == null) return;

        const cx = x(date);
        const isPositive = val >= 0;
        const barTop = isPositive ? yMacd(val) : yMacd(0);
        const barBottom = isPositive ? yMacd(0) : yMacd(val);
        const barHeight = Math.max(1, barBottom - barTop);

        macdG.append('rect')
          .attr('x', cx - barWidth / 2)
          .attr('y', barTop)
          .attr('width', barWidth)
          .attr('height', barHeight)
          .attr('fill', isPositive ? '#16a34a' : '#dc2626')
          .attr('fill-opacity', 0.65)
          .attr('rx', 1);
      });
    }

    // MACD line
    const macdLinePoints = safeData.dates
      .map((date, i) => ({
        date,
        value: safeData.macd.line ? safeData.macd.line[i] : null,
      }))
      .filter((d) => d.value !== null && d.value !== undefined);

    if (macdLinePoints.length > 1) {
      const macdLineFn = d3.line()
        .x((d) => x(d.date))
        .y((d) => yMacd(d.value))
        .curve(d3.curveMonotoneX);

      macdG.append('path')
        .datum(macdLinePoints)
        .attr('d', macdLineFn)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1.5);
    }

    // Signal line
    const signalPoints = safeData.dates
      .map((date, i) => ({
        date,
        value: safeData.macd.signal ? safeData.macd.signal[i] : null,
      }))
      .filter((d) => d.value !== null && d.value !== undefined);

    if (signalPoints.length > 1) {
      const signalLineFn = d3.line()
        .x((d) => x(d.date))
        .y((d) => yMacd(d.value))
        .curve(d3.curveMonotoneX);

      macdG.append('path')
        .datum(signalPoints)
        .attr('d', signalLineFn)
        .attr('fill', 'none')
        .attr('stroke', '#f59e0b')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '5,3');
    }

    // ──────────────────────────────────────────
    // CROSSHAIR + HOVER (spanning both panels)
    // ──────────────────────────────────────────
    const crosshairRsi = rsiG.append('line')
      .attr('y1', 0)
      .attr('y2', rsiInnerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const crosshairMacd = macdG.append('line')
      .attr('y1', 0)
      .attr('y2', macdInnerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // RSI hover dot
    const rsiDot = rsiG.append('circle')
      .attr('r', 4)
      .attr('fill', '#2563eb')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    // Full-width invisible overlay for RSI panel
    rsiG.append('rect')
      .attr('width', innerW)
      .attr('height', rsiInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event))
      .on('mouseleave', () => handleLeave());

    // Full-width invisible overlay for MACD panel
    macdG.append('rect')
      .attr('width', innerW)
      .attr('height', macdInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event))
      .on('mouseleave', () => handleLeave());

    function handleHover(event) {
      const [mouseX] = d3.pointer(event, rsiG.node());
      // Use pointer relative to MACD panel if that's where the event is
      const mx = event.target.closest('g') === macdG.node()
        ? d3.pointer(event, macdG.node())[0]
        : mouseX;

      // Find closest date
      const domainDates = safeData.dates;
      let closestDate = domainDates[0];
      let closestDist = Infinity;
      domainDates.forEach((date) => {
        const dist = Math.abs(x(date) - mx);
        if (dist < closestDist) {
          closestDist = dist;
          closestDate = date;
        }
      });

      const idx = domainDates.indexOf(closestDate);
      const cx = x(closestDate);

      // Show crosshairs on both panels
      crosshairRsi.attr('x1', cx).attr('x2', cx).style('display', null);
      crosshairMacd.attr('x1', cx).attr('x2', cx).style('display', null);

      // Show RSI dot
      const rsiVal = safeData.rsi[idx];
      if (rsiVal != null) {
        rsiDot.attr('cx', cx).attr('cy', yRsi(rsiVal)).style('display', null);
      } else {
        rsiDot.style('display', 'none');
      }

      setTooltip({
        x: event.clientX + 14,
        y: event.clientY - 10,
        date: closestDate,
        rsi: rsiVal,
        macdLine: safeData.macd.line ? safeData.macd.line[idx] : null,
        signal: safeData.macd.signal ? safeData.macd.signal[idx] : null,
        histogram: safeData.macd.histogram ? safeData.macd.histogram[idx] : null,
      });
    }

    function handleLeave() {
      crosshairRsi.style('display', 'none');
      crosshairMacd.style('display', 'none');
      rsiDot.style('display', 'none');
      setTooltip(null);
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [safeData]);

  if (!safeData) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Momentum Indicators</div>
          <div style={styles.subtitle}>RSI and MACD analysis of aggregate pricing trends</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Momentum Indicators</div>
        <div style={styles.subtitle}>RSI and MACD analysis of aggregate pricing trends</div>
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: '#2563eb', borderRadius: 1 }} />
          <span>RSI / MACD Line</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="16" height="10">
            <line x1="0" y1="5" x2="16" y2="5" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="5,3" />
          </svg>
          <span>Signal Line</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 8, height: 10, background: '#16a34a', borderRadius: 1, opacity: 0.65 }} />
          <span>Histogram +</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 8, height: 10, background: '#dc2626', borderRadius: 1, opacity: 0.65 }} />
          <span>Histogram -</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 8, background: '#fecaca', borderRadius: 1, opacity: 0.5 }} />
          <span>Overbought</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 8, background: '#bbf7d0', borderRadius: 1, opacity: 0.5 }} />
          <span>Oversold</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}
          </div>
          {tooltip.rsi != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>RSI</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.rsi >= 70 ? '#dc2626' : tooltip.rsi <= 30 ? '#16a34a' : '#1f2937',
                }}
              >
                {tooltip.rsi.toFixed(1)}
              </span>
            </div>
          )}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
            {tooltip.macdLine != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>MACD</span>
                <span style={{ fontWeight: 600, color: '#2563eb' }}>
                  {tooltip.macdLine.toFixed(3)}
                </span>
              </div>
            )}
            {tooltip.signal != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Signal</span>
                <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                  {tooltip.signal.toFixed(3)}
                </span>
              </div>
            )}
            {tooltip.histogram != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Histogram</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: tooltip.histogram >= 0 ? '#16a34a' : '#dc2626',
                  }}
                >
                  {tooltip.histogram >= 0 ? '+' : ''}{tooltip.histogram.toFixed(3)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
